import { Box, BoxProps } from "@chakra-ui/react";
import {
  cloneElement,
  isValidElement,
  ReactElement,
  useEffect,
  useRef,
} from "react";
import { ReactNode } from "react";

let isYouTubeAPIReady = false;
const youTubeReadyCallbacks: (() => void)[] = [];

function onYouTubeIframeAPIReadyWrapper() {
  isYouTubeAPIReady = true;
  youTubeReadyCallbacks.forEach((cb) => cb());
  youTubeReadyCallbacks.length = 0;
}

if (!window.onYouTubeIframeAPIReady) {
  window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReadyWrapper;
}

type YoutubeIframePlayerProps = {
  children: ReactNode;
  onFirstPlay?: (videoUrl: string) => void;
} & BoxProps;

export function YoutubeIframePlayer({
  children,
  onFirstPlay,
  ...props
}: YoutubeIframePlayerProps) {
  const iframeRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const hasStartedRef = useRef(false);

  const extractVideoId = (src: string): string | null => {
    // Handle various YouTube URL formats
    const patterns = [
      /\/embed\/([a-zA-Z0-9_-]+)/,                    // embed URLs
      /youtu\.be\/([a-zA-Z0-9_-]+)/,                  // youtu.be short URLs
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,       // YouTube Shorts
      /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,      // regular watch URLs
      /youtube\.com\/v\/([a-zA-Z0-9_-]+)/,            // old style /v/ URLs
      /youtube\.com\/watch.*[?&]v=([a-zA-Z0-9_-]+)/,  // watch URLs with other params
      /youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]+)/, // privacy-enhanced embed
    ];
    
    for (const pattern of patterns) {
      const match = src.match(pattern);
      if (match) {
        return match[1];
      }
    }
    
    return null;
  };

  useEffect(() => {
    const initPlayer = () => {
      const iframeEl = iframeRef.current?.firstChild as HTMLIFrameElement;
      if (!iframeEl) return;

      const src = iframeEl.src;
      const videoId = extractVideoId(src);
      if (!videoId) return;

      playerRef.current = new window.YT.Player(iframeEl, {
        videoId,
        events: {
          onStateChange: (event) => {
            if (
              event.data === window.YT.PlayerState.PLAYING &&
              !hasStartedRef.current
            ) {
              hasStartedRef.current = true;
              onFirstPlay?.(src);
            }
          },
        },
      });
    };

    const ensureAPILoaded = () => {
      const existingScript = document.querySelector(
        "script[src='https://www.youtube.com/iframe_api']",
      );

      if (!existingScript) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }

      if (isYouTubeAPIReady) {
        initPlayer();
      } else {
        youTubeReadyCallbacks.push(initPlayer);
      }
    };

    ensureAPILoaded();
  }, [onFirstPlay]);

  if (!isValidElement(children)) return null;

  const iframeElement = children as ReactElement<{ src: string }>;

  const modifiedChild = cloneElement(iframeElement, {
    src: addEnableJsApi(iframeElement.props.src),
  });

  return (
    <Box ref={iframeRef} {...props}>
      {modifiedChild}
    </Box>
  );
}

function addEnableJsApi(src: string): string {
  try {
    const url = new URL(src);
    url.searchParams.set("enablejsapi", "1");
    return url.toString();
  } catch (err) {
    return src;
  }
}
